import { capitalizeAllIfLower, capitalizeIfLower } from '@btime/helpers'
import { filter } from 'ramda'
import { useIsMaster, useIsOwner, useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { RefetchingIndicator } from '../../../../components/helpers/StatePresenter'
import { UserAvatar } from '../../../../components/presentation/Avatar'
import { accountsClient } from '../../../../utils/apollo/api'
import { useGetAllUsers } from '../../../../containers/users/SelectAllUsersLink'
import { useOpenUserModal } from '../../../../utils/routes/modals/modalsActions'
import { useRowActionsColumn } from '../../../../components/tables/Table'
import { useUserActionsList } from '../../../../utils/actions/users/list'
import { useUsersState } from '../_load/UsersLoader'
import AppMigrationTag from '../../../../containers/users/AppMigrationTag'
import DataTable from '../../../../packages/kiti/components/table/DataTable'
import EditableContract from '../../../../containers/contracts/EditableContract'
import EditableUser from '../../../../containers/users/EditableUser'
import FlexGrid from '../../../../components/structure/FlexGrid'
import GroupSelect from '../../../../containers/groups/select/GroupSelect'
import Link from '../../../../components/actions/Link'
import ListCounter from '../../../../components/helpers/ListCounter'
import ProfileSelect from '../../../../containers/profiles/select/ProfileSelect'
import SwitchDisabled from '../../../../components/helpers/disabled/SwitchDisabled'
import Text from '../../../../packages/kiti/components/presentation/Text'
import DeletedTag from '../../../../components/helpers/DeletedTag'

const UsersTable = props => {
  const { t } = useTranslation(['listUsers', 'common'])
  const { users: data } = useUsersState()
  const canEdit = useSessionPermission('editUser')
  const openDetailsModal = useOpenUserModal()
  const isOwner = useIsOwner()
  const onSelectAll = useGetAllUsers()

  const columns = [
    {
      label: t('listUsers:name'),
      headerSideContent: (
        <>
          <ListCounter
            client={accountsClient}
            entity="users"
            filterName="UserFilter"
            size="xsmall"
            bold
            filters={{ includeDisabled: true, hideMasters: !useIsMaster(), ignoreSegments: true }}
          />
          <RefetchingIndicator />
        </>
      ),
      dataIndex: 'name',
      sortIndex: 'NAME',
      flex: 4,
      render: (value, record) => (
        <EditableUser record={record} field="name">
          <Link onClick={() => openDetailsModal(record)} block>
            <FlexGrid justify="flex-start" gutter={0.3}>
              <UserAvatar user={record} />
              <Text bold color="dark2" breakWord>
                {capitalizeAllIfLower(value)}
                {!!record.contract.deleted && <DeletedTag entity={record.contract} />}
              </Text>
            </FlexGrid>
          </Link>
        </EditableUser>
      )
    },

    {
      label: t('listUsers:email'),
      dataIndex: 'email',
      sortIndex: 'EMAIL',
      flex: 3,
      render: (value, record) => (
        <EditableUser record={record} field="email">
          <Text>{value}</Text>
        </EditableUser>
      )
    },

    {
      label: t('listUsers:group'),
      dataIndex: ['contract', 'group', 'name'],
      sortIndex: 'GROUP_NAME',
      flex: 2,
      render: (value, record) => (
        <EditableContract record={record.contract} multiple field="groups" Input={GroupSelect}>
          <Text>{capitalizeIfLower(record?.contract?.groups?.map(group => group.name)?.join(', ') || value)}</Text>
        </EditableContract>
      )
    },

    {
      label: t('listUsers:profile'),
      dataIndex: ['contract', 'profile', 'name'],
      sortIndex: 'PROFILE_NAME',
      flex: 2,
      render: (value, record) => (
        <EditableContract record={record.contract} field="profile" Input={ProfileSelect}>
          <Text>{t('common:profiles.' + value, value)}</Text>
        </EditableContract>
      )
    },
    {
      label: t('listUsers:newApp'),
      dataIndex: ['mobileDetails'],
      sortIndex: 'MOBILE_DETAILS',
      flex: 2,
      render: (value, _) => <AppMigrationTag isUsingNewApp={!!value} />
    },

    {
      label: t('listUsers:enabled'),
      dataIndex: 'enabled',
      sortIndex: 'ENABLED',
      flex: 1,
      render: (_, record) => (
        <SwitchDisabled
          client={accountsClient}
          entity="Contract"
          record={record.contract}
          size={'small'}
          disabled={!canEdit}
        />
      )
    },

    {
      label: t('common:actions'),
      flex: 1,
      render: useRowActionsColumn(useUserActionsList)
    }
  ]

  return (
    <DataTable
      showDeleted={record => record?.contract?.deleted}
      rowKey="id"
      columns={filter(column => (isOwner ? true : column.sortIndex !== 'MOBILE_DETAILS'), columns)}
      data={data}
      inPanel
      size="medium"
      _TODO_onSelectAll={onSelectAll}
      {...props}
    />
  )
}

export default UsersTable
